import { getApiBaseUrl } from '../config/apiConfig';

const API_BASE_URL = getApiBaseUrl();

export const apiRoutes = {
  auth: {
    login: `/Auth/login`,
    logout: `/Auth/logout`,
    refreshToken: `/auth/refresh-token`,
    profile: `/Auth/profile`,
    theme: `/Auth/theme`,
    userConfig: `/Auth/user-config`,
    status: `/auth/status`,
    changePassword: `/Auth/change-password`,
    twoFactor: `/Auth/two-factor`,
    twoFactorEnable: `/Auth/two-factor/enable`,
    twoFactorDisable: `/Auth/two-factor/disable`,
    twoFactorRelink: `/Auth/two-factor/relink`,
    authenticatorApk: `/Auth/authenticator-apk`,
  },

  roles: {
    list: `/Roles`,
  },

  users: {
    list: `/Users/list`,
    getById: (id: number | string) => `/Users/${id}`,
    create: `/Users`,
    update: (id: number | string) => `/Users/${id}`,
    delete: (id: number | string) => `/Users/${id}`,
    resetTwoFactor: (id: number | string) => `/Users/${id}/two-factor/reset`,
    roles: `/Users/roles`,
  },

  tenants: {
    list: `/tenant/list`,
    getById: (id: number | string) => `/Tenant/${id}`,
    create: `/tenant/create`,
    update: (id: number | string) => `/Tenant/${id}`,
    delete: (id: number | string) => `/Tenant/${id}`,
    /** Soft-delete when DELETE body cannot be sent. */
    deleteConfirm: (id: number | string) => `/Tenant/${id}/delete`,
    /** Activate/deactivate tenant (Admin + adminPassword). */
    setStatus: (id: number | string) => `/Tenant/${id}/status`,
    resetData: (id: number | string) => `/Tenant/${id}/reset-data`,
    resetPassword: (id: number | string) => `/Tenant/${id}/reset-password`,
    resetUserPassword: (tenantId: number | string, userId: number | string) =>
      `/Tenant/${tenantId}/users/${userId}/reset-password`,
    userList: (id: number | string) => `/Tenant/${id}/users`,
    /** Master DB: activity counts per tenant for the last days + hours (GET). */
    tenantWiseCounts: (days: number, hours: number) =>
      `/tenant-wise-counts?days=${encodeURIComponent(String(days))}&hours=${encodeURIComponent(String(hours))}`,
    tenantWiseCountsLastHour: `/tenant-wise-counts/last-hour`,
  },

  sodas: {
    list: `/sodas`,
    getById: (id: number | string) => `/sodas/${id}`,
    create: `/Soda`,
    byGame: (gameId: number | string) => `/Soda/by-game/${gameId}`,
    byGameChanges: (gameId: number | string) => `/Soda/by-game/${gameId}/changes`,
    partnerSummary: (gameId: number | string) => `/soda/partner-summary/${gameId}`,
    confirmPartner: (gameId: number | string, partnerId: number | string) =>
      `/soda/${gameId}/partner/${partnerId}/confirm`,
    partnerTransactions: (gameId: number | string, partnerId: number | string) =>
      `/soda/${gameId}/partner/${partnerId}/transactions`,
    confirmPartnerTransaction: (
      gameId: number | string,
      partnerId: number | string,
      transactionId: number | string,
    ) => `/soda/${gameId}/partner/${partnerId}/transactions/${transactionId}/confirm`,
    runningByParty: (gameId: number | string) =>
      `${API_BASE_URL}/soda/party-summary/${gameId}`,
    confirmPartyTally: (gameId: number | string, partyId: number | string) =>
      `${API_BASE_URL}/soda/party-summary/${gameId}/party/${partyId}/confirm`,
    partyConfirmation: () => `${API_BASE_URL}/soda/party-confirmation`,
    partyDetail: (gameId: number | string, partyId: number | string, isPartnerSoda?: boolean) => {
      const baseUrl = `/Soda/detail?gameId=${gameId}&partyId=${partyId}`;
      return isPartnerSoda !== undefined ? `${baseUrl}&isPartnerSoda=${isPartnerSoda}` : baseUrl;
    },
    confirmSoda: () => `/Soda/confirm`,
    update: (id: number | string) => `/sodas/${id}`,
    updateSoda: (id: number | string) => `/Soda/${id}`,
    delete: (id: number | string) => `/soda/${id}`,
  },

  games: {
    create: `/game`,
    list: `/Game/list`,
    getById: (id: number | string) => `/game/${id}`,
    update: (id: number | string) => `/game/${id}`,
    delete: (id: number | string) => `/game/${id}`,
    config: (id: number | string) => `/Game/${id}/config`,
    matchOver: `/Match/over`,
    sessionMatchOver: `/match/session-over`,
    matchResults: (id: number | string) => `/Game/${id}/results`,
    matchRecall: (id: number | string) => `/match/recall/${id}`,
    teamCuts: (gameId: number | string) => `/Game/${gameId}/team-cuts`,
    /** DELETE one row by primary key (`id`), returns remaining cuts (same shape as GET). */
    teamCutById: (gameId: number | string, cutId: number | string) =>
      `/Game/${gameId}/team-cuts/${cutId}`,
  },

  parties: {
    list: `/Party`,
    lite: `/Party/lite`,
    getById: (id: number | string) => `/Party/${id}`,
    getByCode: (code: string) => `/Party/code/${code}`,
    create: `/Party`,
    update: (id: number | string) => `/Party/${id}`,
    delete: (id: number | string) => `/Party/${id}`,
  },

  reports: {
    partyAccount: `/Party/account-report`,
    profitLossReport: `/profit-loss-report`,
    validateParty: `/Transaction/ValidateParty`,
    validateEndDate: `/Report/ValidateEndDate`,
    updateAllIsTallyForParty: `/Report/UpdateAllIsTallyForParty`,
    updateHavalaIsTally: (havalaId: number | string) => `/Report/UpdateHavalaIsTally?Id=${havalaId}`,
    updateIsTally: (id: number | string) => `/Report/UpdateIsTally?Id=${id}`,
    getSodaDetailByPartyId: (matchId: number | string, matchTypeIdEncrypt: string, partyIdEncrypt: string) => 
      `/Report/GetSodaDetailbyPartyId?id=${matchId}&&matchTypeIdEncrypt=${matchTypeIdEncrypt}&&partyIdEncrypt=${partyIdEncrypt}`,
    exportLedgerReport: `/Report/ExportLedgerReportToPDF`,
  },

  /** GET: active message for banner; POST: create broadcast (master DB). */
  notification: `/notification`,
  notifications: {
    create: `/notification`,
    /** DELETE all notifications; optional tenantId query scopes to one tenant. */
    clearAll: (tenantId?: number) =>
      tenantId != null ? `/notifications?tenantId=${tenantId}` : `/notifications`,
    deleteOne: (id: number) => `/notifications/${id}`,
    markAllRead: `/notifications/read-all`,
  },

  transactions: {
    createHavala: `/Transaction/havala`,
    listHavala: `/Transaction/havala/list`,
    getHavalaById: (id: number | string) => `/Transaction/havala/${id}`,
    updateHavala: (id: number | string) => `/Transaction/havala/${id}`,
    deleteHavala: (id: number | string) => `/Transaction/havala/${id}`,
    partySummary: `/Transaction/party-summary`,
    clearOpeningBalance: `/Transaction/clear-opening-balance`,
    havalaListByDateRange: `/Transaction/havala/list-by-date-range`,
    confirmParty: `/Transaction/confirm-party`,
    confirmGame: `/Transaction/confirm-game`,
    confirm: `/Transaction/confirm`,
    importAccountFileUpload: `/Transaction/import-account/files`,
    importAccountFileParse: (fileId: number | string) => `/Transaction/import-account/files/${fileId}/parse`,
    importAccountFileDetail: (fileId: number | string) => `/Transaction/import-account/files/${fileId}`,
    importAccountFileDownload: (fileId: number | string) => `/Transaction/import-account/files/${fileId}/download`,
    importAccountUsernameMappings: `/Transaction/import-account/username-mappings`,
    importAccountUsernameMappingLookup: (username: string) =>
      `/Transaction/import-account/username-mappings/lookup?username=${encodeURIComponent(username)}`,
    importAccountUsernameMappingsBulk: `/Transaction/import-account/username-mappings/bulk`,
    importAccountUsernameMappingById: (id: number | string) =>
      `/Transaction/import-account/username-mappings/${id}`,
    importAccountMappings: `/Transaction/import-account/mappings`,
    importAccountPreview: `/Transaction/import-account/preview`,
    importAccountExecute: `/Transaction/import-account`,
    importAccountBatchDetail: (importBatchId: number | string) => `/Transaction/import-account/${importBatchId}`,
  },

  settings: {
    sodaFormSequence: `/Settings/soda-form-sequence`,
  },

  utility: {
    tenantDatabaseBackup: `/Utility/tenant-database-backup`,
  },

  // Add more modules here...
};

/** Query `pageSize` for GET `/Soda/by-game/{gameId}` (fetch enough rows for one game). */
export const SODA_BY_GAME_PAGE_SIZE = 10000;
